import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Eye, EyeOff, X, Check, Info } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface APIKey {
  id: string;
  name: string;
  key: string;
  createdAt: string;
  lastUsed: string | null;
  isPaper: boolean;
}

export default function APIKeysTab() {
  const { toast } = useToast();
  const [showAlpacaKeys, setShowAlpacaKeys] = useState<boolean>(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState<boolean>(false);
  const [newKeyName, setNewKeyName] = useState<string>("");
  const [useTestEnvironment, setUseTestEnvironment] = useState<boolean>(true);

  // Sample API keys data
  const [apiKeys, setApiKeys] = useState<APIKey[]>([
    {
      id: "key_1",
      name: "Alpaca Paper Trading Key",
      key: "PK3A6D7UMTWJ8MZZ1G4U",
      createdAt: "2025-04-15T10:30:00Z",
      lastUsed: "2025-05-06T15:45:00Z",
      isPaper: true
    },
    {
      id: "key_2",
      name: "Alpaca Live Trading Key",
      key: "AK8DG0QZWT7DACDHV927",
      createdAt: "2025-03-01T08:15:00Z",
      lastUsed: "2025-04-28T09:20:00Z",
      isPaper: false
    }
  ]);

  const [formData, setFormData] = useState({
    // Alpaca API Keys (Live Trading)
    alpacaLive: {
      key: "AK8DG0QZWT7DACDHV927",
      secret: "EPAjsSQJbBTyNvvLdnGGZ0m8rM4za1wX7sR5TSzk",
      enabled: true
    },
    // Alpaca API Keys (Paper Trading)
    alpacaPaper: {
      key: "PK3A6D7UMTWJ8MZZ1G4U",
      secret: "Ci7I3g0lfFRb9yI1EZJguiymxWAJfNjEYYMLzCVx",
      enabled: true
    },
  });

  const handleCreateKey = () => {
    if (!newKeyName.trim()) {
      toast({
        title: "Key name required",
        description: "Please provide a name for your API key.",
        variant: "destructive"
      });
      return;
    }

    setIsGeneratingKey(true);

    // Simulate API call
    setTimeout(() => {
      const newKey: APIKey = {
        id: `key_${Date.now()}`,
        name: newKeyName,
        key: `${useTestEnvironment ? 'PK' : 'AK'}${Math.random().toString(36).substring(2, 15).toUpperCase()}`,
        createdAt: new Date().toISOString(),
        lastUsed: null,
        isPaper: useTestEnvironment
      };

      setApiKeys(prev => [newKey, ...prev]);
      setNewKeyName("");
      setIsGeneratingKey(false);

      toast({
        title: "API Key created",
        description: "Your new API key has been generated successfully."
      });
    }, 1000);
  };

  const handleDeleteKey = (id: string) => {
    setApiKeys(prev => prev.filter(key => key.id !== id));
    
    toast({
      title: "API Key deleted",
      description: "The API key has been deleted successfully."
    });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-white mb-2">API Keys</h2>
        <p className="text-stewie-gray">
          Manage your API keys for integration with Alpaca and other services.
        </p>
      </div>

      <div className="settings-card">
        <h3 className="settings-header">Alpaca API Integration</h3>
        
        <div className="space-y-4">
          <div className="bg-stewie-blue/10 border border-stewie-blue/30 rounded-md p-3 text-sm text-stewie-gray flex gap-2 items-start">
            <Info size={18} className="text-stewie-blue mt-0.5 flex-shrink-0" />
            <p>
              Connect to Alpaca to enable live and paper trading. Your API keys are encrypted and securely stored.
            </p>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="alpaca-key" className="text-white">API Key ID</Label>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 text-stewie-gray hover:text-white hover:bg-stewie-dark"
                onClick={() => setShowAlpacaKeys(!showAlpacaKeys)}
              >
                {showAlpacaKeys ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
            
            <div className="relative">
              <Input
                id="alpaca-key"
                value="PK3A6D7UMTWJ8MZZ1G4U"
                type={showAlpacaKeys ? "text" : "password"}
                className="bg-stewie-dark border-stewie-gray/30 text-white pr-24"
                readOnly
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 text-xs text-stewie-blue hover:text-stewie-blue/80 hover:bg-stewie-blue/10"
                  onClick={() => {
                    navigator.clipboard.writeText("PK3A6D7UMTWJ8MZZ1G4U");
                    toast({ title: "Copied to clipboard" });
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="alpaca-secret" className="text-white">API Secret</Label>
            <div className="relative">
              <Input
                id="alpaca-secret"
                value="Ci7I3g0lfFRb9yI1EZJguiymxWAJfNjEYYMLzCVx"
                type={showAlpacaKeys ? "text" : "password"}
                className="bg-stewie-dark border-stewie-gray/30 text-white pr-24"
                readOnly
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 text-xs text-stewie-blue hover:text-stewie-blue/80 hover:bg-stewie-blue/10"
                  onClick={() => {
                    navigator.clipboard.writeText("Ci7I3g0lfFRb9yI1EZJguiymxWAJfNjEYYMLzCVx");
                    toast({ title: "Copied to clipboard" });
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 pt-2">
            <Switch id="use-paper" checked={true} className="data-[state=checked]:bg-stewie-blue" />
            <Label htmlFor="use-paper" className="text-white cursor-pointer">
              Use Paper Trading
            </Label>
          </div>
          
          <div className="flex justify-end pt-2">
            <Button className="bg-stewie-blue hover:bg-stewie-blue/80">
              Update Alpaca Keys
            </Button>
          </div>
        </div>
      </div>

      <div className="settings-card">
        <h3 className="settings-header">Generate New API Key</h3>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="key-name" className="text-white">Key Name</Label>
            <Input
              id="key-name"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="My API Key"
              className="bg-stewie-dark border-stewie-gray/30 text-white"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch 
              id="test-env" 
              checked={useTestEnvironment}
              onCheckedChange={setUseTestEnvironment}
              className="data-[state=checked]:bg-stewie-blue"
            />
            <Label htmlFor="test-env" className="text-white cursor-pointer">
              Test Environment
            </Label>
          </div>
          
          <div className="pt-2">
            <Button 
              onClick={handleCreateKey} 
              disabled={isGeneratingKey} 
              className="bg-stewie-blue hover:bg-stewie-blue/80"
            >
              {isGeneratingKey ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </span>
              ) : "Generate Key"}
            </Button>
          </div>
        </div>
      </div>

      <div className="settings-card">
        <h3 className="settings-header">Your API Keys</h3>
        
        <div className="space-y-4">
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-stewie-gray">
              <p>You haven't created any API keys yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => (
                <div 
                  key={key.id} 
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-stewie-dark/50 border border-stewie-gray/10 rounded-md"
                >
                  <div className="flex-grow">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white">{key.name}</span>
                      {key.isPaper ? (
                        <span className="bg-stewie-blue/20 text-stewie-blue text-xs px-1.5 py-0.5 rounded">Test</span>
                      ) : (
                        <span className="bg-stewie-accent/20 text-stewie-accent text-xs px-1.5 py-0.5 rounded">Live</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <code className="text-sm bg-stewie-charcoal text-stewie-gray py-0.5 px-1 rounded font-mono">
                        {showAlpacaKeys ? key.key : key.key.substring(0, 4) + '••••••••••'}
                      </code>
                      <span className="text-xs text-stewie-gray">
                        Created: {formatDate(key.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-center">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="h-8 text-xs bg-transparent border-stewie-gray/20 text-stewie-gray hover:bg-stewie-gray/10 hover:text-white"
                      onClick={() => {
                        navigator.clipboard.writeText(key.key);
                        toast({ title: "Copied to clipboard" });
                      }}
                    >
                      Copy
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      className="h-8 text-xs bg-transparent border border-red-800/30 text-red-400 hover:bg-red-900/20 hover:text-red-300"
                      onClick={() => handleDeleteKey(key.id)}
                    >
                      <X size={14} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
